import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Zap,
  Play,
  Pause,
  Clock,
  Mail,
  Loader2,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { getAuthHeaders, adminFetch } from '../../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const TRIGGER_OPTIONS = [
  { value: 'new_member', label: 'New Member', description: 'When a customer signs up and opts in to newsletters' },
  { value: 'inactive_30_days', label: 'Inactive 30 Days', description: 'When a member hasn\'t attended in 30 days' },
  { value: 'belt_promotion', label: 'Belt Promotion', description: 'When a student is promoted to a new belt' },
  { value: 'welcome_sequence', label: 'Welcome Sequence', description: 'Manually triggered welcome emails' },
];

export default function NewsletterAutomationsTab({ showMessage }) {
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedRuns, setExpandedRuns] = useState({});
  const [runs, setRuns] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: 'new_member',
    sequence: []
  });

  useEffect(() => {
    fetchAutomations();
    fetchTemplates();
  }, []);

  const fetchAutomations = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/automations`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAutomations(data);
      }
    } catch (error) {
      console.error('Error fetching automations:', error);
      showMessage('Failed to load automations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/automation-templates`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchRuns = async (automationId) => {
    try {
      const response = await adminFetch(
        `${API_BASE}/api/admin/newsletter/automations/${automationId}/runs?limit=10`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setRuns(prev => ({ ...prev, [automationId]: data.runs }));
      }
    } catch (error) {
      console.error('Error fetching runs:', error);
    }
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      name: '',
      description: '',
      trigger: 'new_member',
      sequence: []
    });
    setShowEditor(true);
  };

  const handleEdit = (automation) => {
    setEditing(automation);
    setFormData({
      name: automation.name,
      description: automation.description || '',
      trigger: automation.trigger,
      sequence: automation.sequence || []
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showMessage('Automation name is required', 'error');
      return;
    }

    if (formData.sequence.length === 0) {
      showMessage('At least one step is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const url = editing
        ? `${API_BASE}/api/admin/newsletter/automations/${editing.id}`
        : `${API_BASE}/api/admin/newsletter/automations`;
      const method = editing ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showMessage(editing ? 'Automation updated' : 'Automation created');
        setShowEditor(false);
        fetchAutomations();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to save automation', 'error');
      }
    } catch (error) {
      console.error('Error saving automation:', error);
      showMessage('Failed to save automation', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (automation) => {
    if (!window.confirm(`Are you sure you want to delete "${automation.name}"? This will cancel all pending runs.`)) {
      return;
    }

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/automations/${automation.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('Automation deleted');
        fetchAutomations();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to delete automation', 'error');
      }
    } catch (error) {
      console.error('Error deleting automation:', error);
      showMessage('Failed to delete automation', 'error');
    }
  };

  const handleToggleEnabled = async (automation) => {
    const action = automation.enabled ? 'disable' : 'enable';
    try {
      const response = await adminFetch(
        `${API_BASE}/api/admin/newsletter/automations/${automation.id}/${action}`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        showMessage(`Automation ${action}d`);
        fetchAutomations();
      } else {
        const error = await response.json();
        showMessage(error.detail || `Failed to ${action} automation`, 'error');
      }
    } catch (error) {
      console.error(`Error ${action}ing automation:`, error);
      showMessage(`Failed to ${action} automation`, 'error');
    }
  };

  const toggleRunsExpanded = (automationId) => {
    const newExpanded = !expandedRuns[automationId];
    setExpandedRuns(prev => ({ ...prev, [automationId]: newExpanded }));
    if (newExpanded && !runs[automationId]) {
      fetchRuns(automationId);
    }
  };

  const addStep = () => {
    setFormData({
      ...formData,
      sequence: [...formData.sequence, { delay_days: 0, template_id: '', subject_override: '' }]
    });
  };

  const updateStep = (index, field, value) => {
    const newSequence = [...formData.sequence];
    newSequence[index] = { ...newSequence[index], [field]: value };
    setFormData({ ...formData, sequence: newSequence });
  };

  const removeStep = (index) => {
    setFormData({
      ...formData,
      sequence: formData.sequence.filter((_, i) => i !== index)
    });
  };

  const getTriggerLabel = (trigger) => {
    return TRIGGER_OPTIONS.find(t => t.value === trigger)?.label || trigger;
  };

  // Editor View
  if (showEditor) {
    return (
      <div className="space-y-6">
        {/* Editor Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setShowEditor(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Automation
          </Button>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Automation Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Welcome Sequence"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="trigger">Trigger *</Label>
            <select
              id="trigger"
              value={formData.trigger}
              onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
            >
              {TRIGGER_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional description..."
          />
        </div>

        {/* Trigger Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <strong>Trigger:</strong> {TRIGGER_OPTIONS.find(t => t.value === formData.trigger)?.description}
        </div>

        {/* Sequence Steps */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Sequence
              </CardTitle>
              <Button size="sm" onClick={addStep}>
                <Plus className="h-4 w-4 mr-1" />
                Add Step
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {formData.sequence.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No steps yet. Add a step to define which emails to send.
              </p>
            ) : (
              <div className="space-y-4">
                {formData.sequence.map((step, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-medium text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Delay (days)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={step.delay_days}
                          onChange={(e) => updateStep(index, 'delay_days', parseInt(e.target.value, 10) || 0)}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {index === 0 ? 'After trigger' : 'After previous step'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs">Email Template *</Label>
                        <select
                          value={step.template_id}
                          onChange={(e) => updateStep(index, 'template_id', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
                        >
                          <option value="">Select template...</option>
                          {templates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.subject}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Subject Override (optional)</Label>
                        <Input
                          value={step.subject_override || ''}
                          onChange={(e) => updateStep(index, 'subject_override', e.target.value)}
                          placeholder="Use template subject"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStep(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Create automated email sequences triggered by events like new signups or inactivity.
        </p>
        <Button onClick={handleCreate} variant="adminPrimary">
          <Plus className="h-4 w-4 mr-2" />
          New Automation
        </Button>
      </div>

      {/* Automations List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : automations.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Zap className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No automations yet</p>
          <p className="text-sm">Create an automation to send emails automatically</p>
        </div>
      ) : (
        <div className="space-y-3">
          {automations.map((automation) => (
            <Card key={automation.id} className={`transition-shadow ${automation.enabled ? 'hover:shadow-md' : 'opacity-75'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">{automation.name}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        automation.enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {automation.enabled ? (
                          <>
                            <Play className="h-3 w-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <Pause className="h-3 w-3" />
                            Paused
                          </>
                        )}
                      </span>
                    </div>
                    {automation.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{automation.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                        <Zap className="h-3 w-3 inline mr-1" />
                        {getTriggerLabel(automation.trigger)}
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                        <Mail className="h-3 w-3 inline mr-1" />
                        {automation.sequence?.length || 0} steps
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {automation.total_runs || 0} runs | {automation.emails_sent || 0} sent
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleEnabled(automation)}
                      className="h-8 w-8 p-0"
                      title={automation.enabled ? 'Disable' : 'Enable'}
                    >
                      {automation.enabled ? (
                        <Pause className="h-4 w-4 text-yellow-600" />
                      ) : (
                        <Play className="h-4 w-4 text-green-600" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(automation)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(automation)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Expandable runs section */}
                <div className="mt-3 pt-3 border-t">
                  <button
                    onClick={() => toggleRunsExpanded(automation.id)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {expandedRuns[automation.id] ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    Recent Runs
                  </button>

                  {expandedRuns[automation.id] && (
                    <div className="mt-2 space-y-1">
                      {runs[automation.id]?.length === 0 ? (
                        <p className="text-xs text-gray-400">No runs yet</p>
                      ) : (
                        runs[automation.id]?.map(run => (
                          <div key={run.id} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                            <div className="flex items-center gap-2">
                              {run.status === 'sent' ? (
                                <CheckCircle className="h-3 w-3 text-green-500" />
                              ) : run.status === 'failed' ? (
                                <AlertCircle className="h-3 w-3 text-red-500" />
                              ) : (
                                <Clock className="h-3 w-3 text-yellow-500" />
                              )}
                              <span>{run.recipient_name || run.recipient_email}</span>
                            </div>
                            <span className="text-gray-400">
                              Step {run.current_step + 1}/{run.total_steps}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
